import { Linkedin } from "lucide-react";
import { Reveal, Overline } from "@/components/Reveal";

const FOUNDERS = [
  {
    photo: "/assets/founder-maxson.png",
    linkedin: "https://www.linkedin.com/in/maxsonlewis/",
    name: "Maxson Alexander Lewis",
    role: "Managing Director",
    focus: "Vision, Strategy & Growth",
    bio: "Maxson sets ElectroHaul's strategic direction — leading capital planning, corporate governance, and industry partnerships. A proven builder of electric-mobility ventures, he brings an extensive network across the automotive, technology, and investment sectors, spanning venture capital, angel, and institutional partners. His command of brand strategy, customer acquisition, and corporate development positions ElectroHaul at the forefront of India's electric freight transformation.",
  },
  {
    photo: "/assets/founder-mohinish.webp",
    linkedin: "https://www.linkedin.com/in/mohinish-scm",
    name: "Mohinish Alahari",
    role: "Chief Operating Officer",
    focus: "Operations & Supply Chain Excellence",
    bio: "Mohinish leads ElectroHaul's operations with over 17 years of leadership in logistics, supply chain management, and fleet operations across India and international markets. He architects scalable operating frameworks — standardized processes, technology-driven execution, and nationwide fleet deployment — with disciplined focus on performance optimization, cost efficiency, and organizational development, ensuring reliable and sustainable electric freight at scale.",
  },
  {
    photo: "/assets/founder-rohit.png",
    linkedin: "https://www.linkedin.com/in/rohit-saily-5569834/",
    name: "Rohit Saily",
    role: "Chief Business Officer",
    focus: "Commercial Strategy & Market Expansion",
    bio: "Rohit drives ElectroHaul's commercial strategy, enterprise customer growth, and strategic partnerships across the EV ecosystem. His track record spans go-to-market design, pricing strategy, revenue growth, and P&L leadership within electric mobility. Combining entrepreneurial experience with deep industry knowledge, he is focused on enduring customer value and accelerating the adoption of electric freight across India.",
  },
];

export default function Leadership() {
  return (
    <section
      id="leadership"
      data-testid="leadership-section"
      className="border-t border-line py-32"
    >
      <div className="mx-auto max-w-[1440px] px-6 md:px-12">
        <Reveal>
          <Overline>Leadership</Overline>
          <h2 className="mt-6 max-w-3xl font-display text-4xl font-semibold tracking-tighter sm:text-5xl lg:text-6xl">
            The minds behind <span className="text-volt">the kilometres.</span>
          </h2>
          <p className="mt-6 max-w-xl text-sm leading-relaxed text-smoke md:text-base">
            A founding team that unites capital strategy, operational depth, and
            commercial execution — purpose-built to electrify freight at national
            scale.
          </p>
        </Reveal>

        <div className="mt-20 grid grid-cols-1 border-l border-t border-line lg:grid-cols-3">
          {FOUNDERS.map((f, i) => (
            <Reveal key={f.name} delay={i * 0.1} className="h-full">
              <article
                data-testid={`founder-card-${i + 1}`}
                className="group relative flex h-full flex-col overflow-hidden border-b border-r border-line p-8 transition-colors duration-500 hover:border-volt/50 md:p-10"
              >
                <span className="absolute inset-x-0 top-0 z-10 h-[2px] origin-left scale-x-0 bg-volt transition-transform duration-500 ease-out group-hover:scale-x-100" />
                <div className="clip-tech relative h-72 w-full overflow-hidden bg-ash">
                  <div className={f.flip ? "h-full w-full -scale-x-100" : "h-full w-full"}>
                    <img
                      src={f.photo}
                      alt={`${f.name}, ${f.role} of ElectroHaul`}
                      data-testid={`founder-photo-${i + 1}`}
                      className="h-full w-full object-cover object-top transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                  </div>
                </div>
                <div className="mt-8 flex items-center justify-between gap-4">
                  <h3 className="font-display text-2xl font-medium tracking-tight">
                    {f.name}
                  </h3>
                  <a
                    href={f.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`${f.name} on LinkedIn`}
                    data-testid={`founder-linkedin-${i + 1}`}
                    className="flex h-9 w-9 shrink-0 items-center justify-center border border-line text-smoke transition-colors duration-300 hover:border-volt hover:text-volt"
                  >
                    <Linkedin className="h-4 w-4" />
                  </a>
                </div>
                <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.2em] text-volt">
                  {f.role}
                </p>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-smoke">
                  {f.focus}
                </p>
                <p className="mt-6 text-sm leading-relaxed text-smoke">{f.bio}</p>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
